import {
  Box,
  Card,
  CardHeader,
  CardBody,
  HStack,
  Text,
  IconButton,
  Stack,
  ButtonGroup,
  Icon,
  Tooltip,
  chakra,
  BoxProps,
  UseRadioProps,
  useStyleConfig,
  useRadio,
  useId,
  Circle,
} from '@chakra-ui/react';
import { useCallback, useMemo, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { GripVerticalIcon, PencilIcon, TrashIcon } from 'lucide-react';
import { SingleChoiceAnswer, CheckIconCustom } from './SingleChoiceAnswer';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { DukeTiptapEditor } from '@duke-hq/ui/components/DukeTiptapEditor/DukeTiptapEditor';
import { unitAssessmentState } from '../UnitAssessmentState';
import { EditorMode } from '@duke-hq/shared-constants/assessments';
import { UnitAssessmentSlides } from '../types';
import { JSONContent } from '@tiptap/core';
import { isDefined } from '@duke-hq/lib';
import { Reorder, useDragControls } from 'framer-motion';
import { UnitAssessmentMultiChoiceQuestionOption } from '@duke-hq/svc-api-db';
import { useSnapshot } from 'valtio';

const List = chakra(Reorder.Group);
const ListItem = chakra(Reorder.Item);

type MultiChoiceQuestionOption = Pick<
  UnitAssessmentMultiChoiceQuestionOption,
  'answer' | 'feedback' | 'isCorrect' | 'position' | 'questionId'
> & {
  id?: string;
};

interface QuestionGroupItemProps {
  index: number;
  questionObject: {
    id: string;
    questionContent: JSONContent;
    options: MultiChoiceQuestionOption[];
    position: number;
    questionGroupId: string;
  };
  handleDeleteMultiChoiceQuestion: (id: string) => void;
  slide: UnitAssessmentSlides[0];
  reorderOptions: any;
}

export function QuestionGroupItem({
  index,
  questionObject,
  handleDeleteMultiChoiceQuestion,
  slide,
  reorderOptions,
}: QuestionGroupItemProps) {
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [options, setOptions] = useState(questionObject.options);
  const correctOptions = questionObject.options.filter((option) => option.isCorrect);

  useMemo(() => {
    setOptions(questionObject.options);

    return () => {
      setOptions([]);
    };
  }, [questionObject.options]);

  const handleDelete = () => {
    handleDeleteMultiChoiceQuestion(questionObject.id);
    setIsAlertOpen(false);
  };

  const saveReorder = useCallback(() => {
    if (!questionObject.id) {
      return;
    }
    const optionIds = options.map((option) => option.id!);
    if (!isDefined(optionIds)) {
      return;
    }
    reorderOptions.mutate({
      questionId: questionObject.id,
      optionIds: optionIds,
    });
  }, [options, questionObject.id, reorderOptions]);

  return (
    <>
      <Card bg={'bg.canvas'}>
        <CardHeader>
          <HStack alignItems="center" justifyContent="space-between" gap={4}>
            <Text fontWeight="semibold" fontSize="sm">
              Question {index + 1}
            </Text>
            <ButtonGroup alignItems="center">
              <Tooltip label={t`Edit question`}>
                <IconButton
                  aria-label={t`Edit question`}
                  variant="ghost"
                  size="sm"
                  icon={<Icon as={PencilIcon} color="fg.default" boxSize="18px" />}
                  onClick={() => {
                    unitAssessmentState.setCurrentSlide(slide);
                    unitAssessmentState.setEditorMode(EditorMode.EDIT);
                    unitAssessmentState.setEditorType('MULTI_CHOICE_QUESTION');
                    unitAssessmentState.setCurrentMultiChoiceQuestionIndex(index);
                    unitAssessmentState.openEditor();
                  }}
                />
              </Tooltip>
              <Tooltip label={t`Delete question`}>
                <IconButton
                  aria-label={t`Delete question`}
                  variant="ghost"
                  size="sm"
                  icon={<Icon as={TrashIcon} color="fg.default" boxSize="18px" />}
                  onClick={() => setIsAlertOpen(true)}
                />
              </Tooltip>
            </ButtonGroup>
          </HStack>
        </CardHeader>
        <CardBody>
          <Stack spacing={4}>
            <DukeTiptapEditor
              initialContent={questionObject.questionContent}
              readOnly
              onChange={() => {}}
            />

            <List
              values={options}
              onReorder={(newOrder: MultiChoiceQuestionOption[]) => {
                setOptions(newOrder);
              }}
              listStyleType="none"
            >
              <SingleChoiceAnswer value={correctOptions[0]?.answer} spacing="3">
                {options.map((option) => (
                  <DraggableSingleChoiceAnswerCard
                    key={option.id}
                    option={option}
                    saveReorder={saveReorder}
                    value={option.answer}
                  />
                ))}
              </SingleChoiceAnswer>
            </List>
          </Stack>
        </CardBody>
      </Card>

      <ConfirmationDialog
        isOpen={isAlertOpen}
        onClose={() => setIsAlertOpen(false)}
        title={<Trans>Delete Question</Trans>}
        body={
          <Trans>You are about to delete this question. Are you sure you want to continue?</Trans>
        }
        onConfirm={handleDelete}
        confirmLabel={<Trans>Delete</Trans>}
        variant="destructive"
      />
    </>
  );
}

interface DraggableSingleChoiceAnswerCardProps extends BoxProps {
  value: string;
  radioProps?: UseRadioProps;
  option: MultiChoiceQuestionOption;
  saveReorder: () => void;
}

function DraggableSingleChoiceAnswerCard(props: DraggableSingleChoiceAnswerCardProps) {
  const unitAssessmentStateSnap = useSnapshot(unitAssessmentState);
  const dragControls = useDragControls();
  const { radioProps, children, option, saveReorder, ...rest } = props;
  const { getInputProps, getRadioProps, state } = useRadio(radioProps);
  const id = useId(undefined, 'radio-button');

  const styles = useStyleConfig('RadioCard', props);
  const inputProps = getInputProps();
  const checkboxProps = getRadioProps();

  return (
    <ListItem
      sx={{
        '.focus-visible + [data-focus]': {
          boxShadow: 'outline',
          zIndex: 1,
        },
      }}
      value={option}
      position="relative"
      dragListener={false}
      dragControls={dragControls}
      onDragEnd={saveReorder}
    >
      <HStack justifyContent="space-between" align="center">
        <input {...inputProps} aria-labelledby={id} disabled />
        <Box sx={styles} {...checkboxProps} {...rest} w="full">
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={4}>
            {state.isChecked ? (
              <Circle bg="brand.500" size="4">
                <Icon as={CheckIconCustom} boxSize="2.5" color="fg.inverted" />
              </Circle>
            ) : (
              <Circle borderWidth="2px" size="4" />
            )}
            <Box flex="1">
              <Stack spacing={2}>
                <Text color="fg.emphasis" textStyle="sm">
                  {option.answer}
                </Text>
                <Box
                  bg={option.isCorrect ? 'green.50' : 'red.50'}
                  p={2}
                  borderRadius={8}
                  hidden={option.feedback === ''}
                >
                  <Text color="fg.muted" fontWeight="semibold" textStyle="xs">
                    <Trans>Feedback</Trans>
                  </Text>
                  <Text color="fg.subtle" textStyle="sm">
                    {option.feedback}
                  </Text>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </Box>
        <Tooltip label={t`Reorder`} hidden={unitAssessmentStateSnap.isEditorOpen}>
          <IconButton
            _hover={{ color: 'brand.600' }}
            icon={<Icon as={GripVerticalIcon} />}
            cursor="grab"
            size="sm"
            variant="tertiary"
            aria-label={t`Reorder options`}
            onPointerDown={(e) => dragControls.start(e)}
            hidden={unitAssessmentStateSnap.isEditorOpen}
          />
        </Tooltip>
      </HStack>
    </ListItem>
  );
}
